/**
 * @file Geometries des munitions (projectiles 3D bouffis + missile + sprites de particules) : construites UNE fois via MergeBuilder,
 *       partagees via GeometryLibrary.
 *
 * Couleurs de sommet des projectiles (voir ToonKit.ammoToon) : r = melange principale -> secondaire, g = ombre.
 * Couleurs de sommet des sprites : gris = ombre (multiplie la couleur d'instance) ; le contour sombre est un calque agrandi derriere.
 * Convention : projectiles orientes en +Z (capsule, diamant, missile) ; formes plates (etoile, coeur, poisson) dessinees dans le plan XY,
 * pointe / tete vers +X, face a +Z (elles sont orientees "face camera" par le rendu).
 */
import {
  CapsuleGeometry,
  CircleGeometry,
  Color,
  CylinderGeometry,
  ExtrudeGeometry,
  OctahedronGeometry,
  PlaneGeometry,
  RingGeometry,
  Shape,
  ShapeGeometry,
  SphereGeometry,
  Vector2,
} from 'three';
import type { BufferGeometry } from 'three';
import type { AmmoShape } from '@config/types';
import { MergeBuilder } from '../factories/MergeBuilder';

/** Couleur de sommet d'un projectile (r = melange principale -> secondaire, g = ombre). */
const vc = (mix: number, shade = 1): Color => new Color().setRGB(mix, shade, 0);
const MAIN = vc(0);
const ACCENT = vc(1);

export interface ShapeParts {
  /** Geometrie coloree (groupe 'main'). */
  fill: BufferGeometry;
  /** Source de la coque de contour (null = utiliser `fill`). A disposer par l'appelant apres ToonKit.hullGeometry. */
  hull: BufferGeometry | null;
}

/** Extrusion bouffie (bevel arrondi) d'un polygone, centree sur z = 0. */
const puffy = (pts: Vector2[], depth: number, bevel: number): BufferGeometry => {
  const g = new ExtrudeGeometry(new Shape(pts), { depth, bevelEnabled: true, bevelThickness: bevel, bevelSize: bevel, bevelSegments: 2, curveSegments: 10, steps: 1 });
  g.translate(0, 0, -depth / 2);
  return g;
};

const polar = (n: number, radius: (i: number) => number, rot = 0): Vector2[] => {
  const pts: Vector2[] = [];
  for (let i = 0; i < n; i++) {
    const a = rot + (i / n) * Math.PI * 2;
    const r = radius(i);
    pts.push(new Vector2(Math.cos(a) * r, Math.sin(a) * r));
  }
  return pts;
};

/** Coeur parametrique centre sur l'origine ; `tip` = angle (rad) vers lequel pointe la pointe (-PI/2 = vers le bas, 0 = vers +X). */
const heartPoints = (scale: number, tip: number): Vector2[] => {
  const pts: Vector2[] = [];
  const n = 36;
  const rot = tip + Math.PI / 2;
  const c = Math.cos(rot);
  const s = Math.sin(rot);
  for (let i = 0; i < n; i++) {
    const t = (i / n) * Math.PI * 2;
    const x = (16 * Math.sin(t) ** 3) / 17;
    const y = (13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t)) / 17 + 0.15;
    // la pointe du coeur parametrique est en -y : rotation pour viser `tip`
    pts.push(new Vector2((x * c - y * s) * scale, (x * s + y * c) * scale));
  }
  return pts;
};

const triangle = (ax: number, ay: number, bx: number, by: number, cx: number, cy: number): Vector2[] => [new Vector2(ax, ay), new Vector2(bx, by), new Vector2(cx, cy)];

/** Projectile 3D d'une forme de skin (largeur de reference ~ 0.25). */
export const buildProjectileShape = (shape: AmmoShape): ShapeParts => {
  const b = new MergeBuilder();
  const free: BufferGeometry[] = [];
  const keep = <T extends BufferGeometry>(g: T): T => {
    free.push(g);
    return g;
  };
  switch (shape) {
    case 'capsule': {
      const g = keep(new CapsuleGeometry(0.09, 0.3, 4, 8));
      g.rotateX(Math.PI / 2);
      b.add(g, MAIN);
      break;
    }
    case 'orb':
      b.add(keep(new SphereGeometry(0.12, 10, 8)), MAIN);
      break;
    case 'diamond':
      b.add(keep(new OctahedronGeometry(1, 0)), MAIN, { sx: 0.125, sy: 0.125, sz: 0.28 });
      break;
    case 'star':
      b.add(keep(puffy(polar(10, (i) => (i % 2 === 0 ? 0.21 : 0.1)), 0.07, 0.03)), MAIN);
      b.add(keep(new SphereGeometry(0.05, 8, 6)), ACCENT, { z: 0.06, outline: false });
      break;
    case 'heart':
      b.add(keep(puffy(heartPoints(0.2, 0), 0.07, 0.03)), MAIN);
      break;
    case 'fish': {
      // corps (principale), queue + nageoires (secondaire), yeux (sombres) ; tete vers +X
      const body = new Shape();
      body.absellipse(0.03, 0, 0.2, 0.125, 0, Math.PI * 2, false, 0);
      const bodyGeo = keep(new ExtrudeGeometry(body, { depth: 0.07, bevelEnabled: true, bevelThickness: 0.03, bevelSize: 0.025, bevelSegments: 2, curveSegments: 14 }));
      bodyGeo.translate(0, 0, -0.035);
      b.add(bodyGeo, MAIN);
      b.add(keep(puffy(triangle(-0.14, 0, -0.34, 0.14, -0.34, -0.14), 0.04, 0.02)), ACCENT);
      b.add(keep(puffy(triangle(-0.05, 0.1, 0.07, 0.1, -0.08, 0.24), 0.04, 0.018)), ACCENT);
      b.add(keep(puffy(triangle(-0.04, -0.1, 0.05, -0.1, -0.07, -0.2), 0.03, 0.015)), ACCENT);
      const eye = vc(0, 0.12);
      b.add(keep(new SphereGeometry(0.03, 8, 6)), eye, { x: 0.13, y: 0.04, z: 0.075, outline: false });
      b.add(keep(new SphereGeometry(0.03, 8, 6)), eye, { x: 0.13, y: 0.04, z: -0.075, outline: false });
      break;
    }
  }
  const fill = b.fill('main');
  if (!fill) throw new Error(`AmmoShapes: forme vide ${shape}`);
  const hull = b.hull();
  for (const g of free) g.dispose();
  return { fill, hull };
};

/** Corps de missile bicolore (comme l'ancien : fuselage + nez + 3 ailerons) : nez + ailerons = principale, fuselage = secondaire. +Z. */
export const buildRocket = (): ShapeParts => {
  const b = new MergeBuilder();
  const body = new CylinderGeometry(0.11, 0.11, 0.5, 8);
  const nose = new CylinderGeometry(0, 0.11, 0.24, 8);
  const fin = new CylinderGeometry(0, 0.07, 0.2, 5);
  b.add(body, ACCENT, { rx: Math.PI / 2 });
  b.add(nose, MAIN, { rx: Math.PI / 2, z: 0.37 });
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + Math.PI / 6;
    b.add(fin, MAIN, { x: Math.sin(a) * 0.12, y: Math.cos(a) * 0.12, z: -0.26, rx: -Math.PI / 2 });
  }
  body.dispose();
  nose.dispose();
  fin.dispose();
  const fill = b.fill('main');
  if (!fill) throw new Error('AmmoShapes: missile vide');
  return { fill, hull: b.hull() };
};

// ---------------------------------------------------------------------------------------------------------------------
// Sprites de particules (face camera, plan XY, rayon ~1)

export type SpriteShape = 'heart' | 'star' | 'sparkle' | 'petal' | 'bubble' | 'flake' | 'flame' | 'dot' | 'note' | 'bolt';
/** Sprites du FxManager (un InstancedMesh chacun). */
export const SPRITE_SHAPES: readonly SpriteShape[] = ['heart', 'star', 'sparkle', 'petal', 'bubble', 'flake', 'flame'];
/** Sprites supplementaires propres a l'aura (AuraRenderer). */
export const AURA_SPRITE_SHAPES: readonly SpriteShape[] = ['dot', 'note', 'bolt'];

const gray = (v: number): Color => new Color().setRGB(v, v, v);
const OUTLINE_SHADE = 0.36;
const OUTLINE_GROW = 1.28;

const scaled = (pts: Vector2[], k: number): Vector2[] => pts.map((p) => new Vector2(p.x * k, p.y * k));

/** Sprite fait de plusieurs pieces : tous les contours (agrandis) derriere, toutes les pieces pleines devant. */
const layeredWithOutline = (b: MergeBuilder, free: BufferGeometry[], pieces: Vector2[][]): void => {
  for (const pts of pieces) {
    const outline = new ShapeGeometry(new Shape(scaled(pts, OUTLINE_GROW)), 6);
    free.push(outline);
    b.add(outline, gray(OUTLINE_SHADE), { z: -0.01 });
  }
  for (const pts of pieces) {
    const fill = new ShapeGeometry(new Shape(pts), 6);
    free.push(fill);
    b.add(fill, gray(1));
  }
};

/** Ellipse polygonale (centre cx, cy ; rayons rx, ry ; inclinee de `rot`). */
const ellipsePoints = (cx: number, cy: number, rx: number, ry: number, rot: number, n = 16): Vector2[] => {
  const c = Math.cos(rot);
  const s = Math.sin(rot);
  const pts: Vector2[] = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    const x = Math.cos(a) * rx;
    const y = Math.sin(a) * ry;
    pts.push(new Vector2(cx + x * c - y * s, cy + x * s + y * c));
  }
  return pts;
};

/** Sprite plein (ombre 1) + calque de contour sombre agrandi derriere. */
const flatWithOutline = (b: MergeBuilder, free: BufferGeometry[], pts: Vector2[]): void => {
  const outline = new ShapeGeometry(new Shape(scaled(pts, OUTLINE_GROW)), 6);
  const fill = new ShapeGeometry(new Shape(pts), 6);
  free.push(outline, fill);
  b.add(outline, gray(OUTLINE_SHADE), { z: -0.01 });
  b.add(fill, gray(1));
};

export const buildSprite = (shape: SpriteShape): BufferGeometry => {
  const b = new MergeBuilder();
  const free: BufferGeometry[] = [];
  switch (shape) {
    case 'heart':
      flatWithOutline(b, free, heartPoints(0.92, -Math.PI / 2));
      break;
    case 'star':
      flatWithOutline(b, free, polar(10, (i) => (i % 2 === 0 ? 1 : 0.5), Math.PI / 2));
      break;
    case 'sparkle':
      flatWithOutline(b, free, polar(8, (i) => (i % 2 === 0 ? 1 : 0.24), Math.PI / 2));
      break;
    case 'petal': {
      const right: Vector2[] = [];
      const left: Vector2[] = [];
      const n = 8;
      for (let i = 0; i <= n; i++) {
        const y = -1 + (2 * i) / n;
        const w = 0.62 * Math.sqrt(Math.max(0, 1 - y * y)) * (1 - 0.22 * y);
        right.push(new Vector2(w, y));
        left.unshift(new Vector2(-w, y));
      }
      flatWithOutline(b, free, [...right.slice(0, -1), ...left.slice(0, -1)]);
      break;
    }
    case 'flame': {
      // goutte pointue vers le haut (+Y), un peu penchee : flamme mignonne
      const pts: Vector2[] = [];
      const n = 28;
      for (let i = 0; i < n; i++) {
        const t = (i / n) * Math.PI * 2;
        pts.push(new Vector2(0.78 * Math.sin(t) * Math.sin(t / 2) ** 1.3 + 0.12 * Math.max(0, Math.cos(t)), Math.cos(t) * 0.95 - 0.05));
      }
      flatWithOutline(b, free, pts);
      break;
    }
    case 'dot':
      flatWithOutline(b, free, polar(14, () => 1));
      break;
    case 'note': {
      // croche : tete ovale inclinee, hampe, drapeau
      layeredWithOutline(b, free, [
        ellipsePoints(-0.28, -0.58, 0.4, 0.29, 0.45),
        [new Vector2(0.06, -0.5), new Vector2(0.26, -0.5), new Vector2(0.26, 0.95), new Vector2(0.06, 0.95)],
        [new Vector2(0.26, 0.95), new Vector2(0.85, 0.5), new Vector2(0.78, 0.1), new Vector2(0.26, 0.5)],
      ]);
      break;
    }
    case 'bolt':
      // eclair en zigzag, pointe vers le bas
      flatWithOutline(b, free, [
        new Vector2(0.1, 1),
        new Vector2(0.58, 1),
        new Vector2(0.2, 0.22),
        new Vector2(0.58, 0.22),
        new Vector2(-0.32, -1),
        new Vector2(-0.04, -0.12),
        new Vector2(-0.5, -0.12),
      ]);
      break;
    case 'bubble': {
      const ring = new RingGeometry(0.68, 1, 22);
      const shine = new CircleGeometry(0.15, 8);
      free.push(ring, shine);
      b.add(ring, gray(0.82));
      b.add(shine, gray(1), { x: -0.36, y: 0.38, z: 0.002 });
      break;
    }
    case 'flake': {
      for (let i = 0; i < 3; i++) {
        const bar = new PlaneGeometry(2, 0.26);
        const edge = new PlaneGeometry(2.14, 0.4);
        free.push(bar, edge);
        b.add(edge, gray(OUTLINE_SHADE), { rz: (i * Math.PI) / 3, z: -0.01 });
        b.add(bar, gray(1), { rz: (i * Math.PI) / 3 });
      }
      break;
    }
  }
  const fill = b.fill('main');
  if (!fill) throw new Error(`AmmoShapes: sprite vide ${shape}`);
  for (const g of free) g.dispose();
  return fill;
};
